import type { InventoryScreenRow } from './inventoryView';

/**
 * One location's stock as a physical count reads it [SSD-7553]. Every figure is summed from the location's own rows:
 * `onHand` is what the location physically holds, and `reserved` and `available` split that same quantity, so a
 * reserved unit is never added on top of what is on the shelf.
 */
export interface LocationStock {
  locationCode: string;
  /** The skus the location holds or still has reserved, one row each. */
  rows: InventoryScreenRow[];
  onHand: number;
  reserved: number;
  /**
   * The server's own on hand minus the active reservations on this location, summed as sent: subtracting `reserved`
   * again would count every reservation twice, and a virtual location such as WH#00 offers none however much it holds.
   */
  available: number;
}

/** A row the count has to look at: stock on the location, or a reservation still standing on an emptied one. */
const holdsSomething = (row: InventoryScreenRow): boolean => row.onHand !== 0 || row.reserved > 0;

/** The rows of exactly one location, and what they add up to; another location holding the same sku stays out. */
export const locationStock = (rows: InventoryScreenRow[], locationCode: string): LocationStock => {
  const here = rows
    .filter((row) => row.locationCode === locationCode && holdsSomething(row))
    .sort((a, b) => a.sku.localeCompare(b.sku));
  return {
    locationCode,
    rows: here,
    onHand: here.reduce((total, row) => total + row.onHand, 0),
    reserved: here.reduce((total, row) => total + row.reserved, 0),
    available: here.reduce((total, row) => total + row.available, 0),
  };
};
